/**
 * File Garbage Collection — safely removes orphaned files from db.files.
 *
 * Scans Y.Doc (the synced CRDT — the only source of truth for document content)
 * for `totodo-file://` references. Files not referenced by any Y.Doc, not owned (`parentId`) by an
 * object that still exists (checklist photos, API attachments), and older
 * than 7 days are deleted.
 *
 * SAFETY MODEL (defense in depth — 5 safety gates):
 *
 * 1. SYNC GATE: GC waits for Dexie Cloud `syncState.phase === "in-sync"`
 *    before running — ensures all server data is pulled before we scan.
 *
 * 2. WEEKLY THROTTLE: Reads `db.setting.get("#last-file-gc")` (synced).
 *    Skips if GC ran within the last 7 days on ANY device.
 *
 * 3. CROSS-TASK Y.Doc SCAN: Scans ALL tasks' Y.Docs (not docHtml!) — a file
 *    is only orphaned if NO task references it in its synced Y.Doc.
 *
 * 4. AGE GATE (7 days): Files must be older than 7 days to be eligible.
 *    This protects against sync lag, partial batches, stale devices,
 *    or timing issues across devices.
 *
 * 5. RE-VERIFY GATE: Right before deleting, re-scan file primaryKeys to
 *    ensure no new files appeared during the scan.
 *
 * WHY Y.Doc (not docHtml):
 * - docHtml is an unsyncedProperty — local cache, not synced between devices.
 *   Basing destructive decisions on it is fundamentally unsafe.
 * - Y.Doc is the synced CRDT — if a file is referenced on ANY device, it's
 *   referenced in the Y.Doc on ALL devices after sync.
 *
 * WHY 7 DAYS (not 1 hour):
 * - A device offline for days that syncs won't accidentally delete files
 *   created recently on another device.
 * - Storage cost of keeping orphans 7 extra days is negligible.
 *
 * @see docs/plans/FILE_GC_PLAN.md — Full design and rationale
 */

import { db } from "@/db/schema"
import { parseDbDate, type DbFile } from "@totodo/operations"
import { yDocToProsemirrorJSON } from "@tiptap/y-tiptap"
import * as Y from "yjs"
import { uniqueIndexKeys } from "@/db/uniqueIndexKeys"

const TOTODO_FILE_PREFIX = "totodo-file://"

/** y-dexie updates table: $<table>.<prop>_updates */
const UPDATES_TABLE = "$totodo.doc_updates"

/** Files must be older than this to be eligible for GC. */
const GC_GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

/** Minimum interval between GC runs (synced across devices). */
const GC_THROTTLE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

/** Setting key for the synced GC timestamp. */
const GC_SETTING_KEY = "#last-file-gc"

/**
 * A file row without its content: Dexie Cloud resolves offloaded binaries on every read (one download
 * per file), and the GC only needs the row's parent and date.
 */
function readFileWithoutContent(fileId: string): Promise<DbFile | undefined> {
  return db.transaction("r", db.files, (tx) => {
    ;(tx as unknown as { idbtrans: { disableBlobResolve?: boolean } }).idbtrans.disableBlobResolve = true
    return db.files.get(fileId)
  })
}

/**
 * Extract all totodo-file:// IDs from a JSON string.
 */
function extractFileIdsFromJson(jsonStr: string): string[] {
  const ids: string[] = []
  let searchFrom = 0
  while (true) {
    const idx = jsonStr.indexOf(TOTODO_FILE_PREFIX, searchFrom)
    if (idx === -1) break
    const idStart = idx + TOTODO_FILE_PREFIX.length
    let idEnd = idStart
    while (idEnd < jsonStr.length) {
      const ch = jsonStr[idEnd]!
      if (ch === '"' || ch === "'" || ch === ">" || ch === " " || ch === "\n" || ch === "\\") break
      idEnd++
    }
    const id = jsonStr.slice(idStart, idEnd)
    if (id) ids.push(id)
    searchFrom = idEnd
  }
  return ids
}

/**
 * Reconstruct a Y.Doc from stored updateV2 rows.
 */
function reconstructYDoc(updates: { u: Uint8Array }[]): Y.Doc {
  const doc = new Y.Doc({ gc: true })
  for (const update of updates) {
    Y.applyUpdateV2(doc, update.u)
  }
  return doc
}

/**
 * Scan all Y.Docs for totodo-file:// references.
 * Returns the set of all referenced file IDs.
 */
async function scanYDocsForFileRefs(): Promise<Set<string>> {
  const referencedIds = new Set<string>()

  // Get unique task IDs that have Y.Doc data
  const updatesTable = db.table(UPDATES_TABLE)
  const taskIds = (await uniqueIndexKeys(updatesTable.orderBy("k"))).filter(
    (k): k is string => typeof k === "string",
  )

  // Reconstruct each Y.Doc and scan for file references
  for (const taskId of taskIds) {
    try {
      const updates = await updatesTable.where({ k: taskId }).toArray()
      if (!updates || updates.length === 0) continue

      const doc = reconstructYDoc(updates)
      try {
        const json = yDocToProsemirrorJSON(doc, "default")
        const jsonStr = JSON.stringify(json)
        for (const id of extractFileIdsFromJson(jsonStr)) {
          referencedIds.add(id)
        }
      } finally {
        doc.destroy()
      }
    } catch {
      // Skip individual docs that fail — don't abort the whole scan
    }
  }

  return referencedIds
}

export interface FileGCResult {
  scanned: number
  orphaned: number
  deleted: number
  skipped: boolean
  reason?: string
}

/**
 * Garbage-collect orphaned files from db.files.
 *
 * Called from the background task runner (fileGcTask.ts).
 * Can also be called manually from dev tools.
 */
export async function cleanOrphanedFiles(): Promise<FileGCResult> {
  const skip = (reason: string): FileGCResult => {
    console.info(`[FileGC] Skipping — ${reason}`)
    return { scanned: 0, orphaned: 0, deleted: 0, skipped: true, reason }
  }

  // SAFETY GATE 1: Weekly throttle — check synced timestamp
  try {
    const lastGc = await db.setting.get(GC_SETTING_KEY)
    if (lastGc?.value) {
      const elapsed = Date.now() - Number(lastGc.value)
      if (elapsed < GC_THROTTLE_MS) {
        return skip(`GC ran ${Math.round(elapsed / 86_400_000)}d ago (throttle: 7d)`)
      }
    }
  } catch {
    // Setting doesn't exist yet — first run, continue
  }

  // SAFETY GATE 2: Get all file primary keys (no blob data loaded!)
  const allFileIds = await db.files.toCollection().primaryKeys()
  const scanned = allFileIds.length
  if (scanned === 0) {
    return { scanned: 0, orphaned: 0, deleted: 0, skipped: false }
  }

  // SAFETY GATE 3: Scan ALL Y.Docs for file references
  const referencedIds = await scanYDocsForFileRefs()

  // SAFETY GATE 4: Find orphans — not referenced by a document, not owned by an object that still
  // exists, AND older than 7 days
  const now = Date.now()
  const orphanIds: string[] = []
  for (const fileId of allFileIds) {
    if (referencedIds.has(fileId as string)) continue

    try {
      const file = await readFileWithoutContent(fileId as string)
      if (!file) continue
      // Checklist photos and files attached through the API hang on their object by parentId, not by a
      // document reference: they stay while that object exists (in the trash too, it can come back).
      if (file.parentId && (await db.totodo.get(file.parentId))) continue
      // createdAt is ISO from the app's uploads and stringified ms from copies; a date we cannot read
      // is never "old enough".
      const created = parseDbDate(file.createdAt)
      if (created === undefined || now - created < GC_GRACE_PERIOD_MS) continue
      orphanIds.push(fileId as string)
    } catch {
      // Skip files we can't read
    }
  }

  if (orphanIds.length === 0) {
    // Save timestamp even when nothing to delete — prevents re-scanning
    await saveGcTimestamp()
    return { scanned, orphaned: 0, deleted: 0, skipped: false }
  }

  // SAFETY GATE 5: Re-verify — check file count hasn't changed
  const recheckIds = await db.files.toCollection().primaryKeys()
  if (recheckIds.length !== allFileIds.length) {
    return skip(`File count changed during scan (${allFileIds.length} → ${recheckIds.length}) — aborting`)
  }

  // Delete verified orphans
  console.info(
    `[FileGC] Deleting ${orphanIds.length} orphaned files ` +
      `(older than 7d, not referenced by any Y.Doc out of ${referencedIds.size} refs)`,
  )
  await db.files.bulkDelete(orphanIds)
  await saveGcTimestamp()

  return {
    scanned,
    orphaned: orphanIds.length,
    deleted: orphanIds.length,
    skipped: false,
  }
}

/** Save the GC timestamp in the synced setting table. */
async function saveGcTimestamp(): Promise<void> {
  const timestamp = new Date().toISOString()
  await db.setting.put({
    id: GC_SETTING_KEY,
    value: String(Date.now()),
    device: "",
    createdAt: timestamp,
    createdBy: "system",
    updatedAt: timestamp,
    updatedBy: "system",
  })
}
